<template>
<!-- eslint-disable max-len -->
  <edit-page
    name="Gamulation Branding"
    model-name="environment"
    param="environmentId"
    page-prefix="/blade/environments"
    :create="createEnvironment"
    :fetch="getEnvironment"
    :fetch-types="getEnvironmentTypes"
    :update="updateEnvironment"
    :remove="deleteEnvironment"
    :enum-names="['characters', 'variants']"
    :stub="environment"
    :validator="validator"
    :utils="utils">
    <template slot-scope="{ model: environment, enums: { characters, variants, }, validator }">
      <!-- metadata -->
      <text-input label v-model.trim="environment.name" :validator="validator.name" required autofocus>
        Name
      </text-input>

      <checkbox-input v-model="environment.active">
        Active
      </checkbox-input>

      <!-- global settings -->
      <text-input label v-model.trim="environment.tabText" :validator="validator.tabText" required>
        Browser Tab Text
      </text-input>

      <select-input label v-model="environment.reasoningInputOverride" :options="reasoningInputOverrideOptions" :validator="validator.reasoningInputOverride" required>Reasoning Input Override (this setting overridden by active workgroup/class)</select-input>

      <img v-if="environment.gameBackgroundImage" class="form-image-background" :src="resolveAssetPath(environment.gameBackgroundImage, 'ui/customer')">
      <asset-select-input use-private-assets label v-model="environment.gameBackgroundImage" legacy-manifest-path="ui/customer">Border Overflow Image</asset-select-input>

      <collapsible-section
        name="Navigation Bar Text/Link Settings"
        id="NBTLS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <text-input label v-model.trim="environment.hcadUrl" :validator="validator.tabText" required no-count>
          Dashboard Link (note: auto-login only works if the Dashboard instance uses the same database as this BLADE instance)
        </text-input>

        <text-input label v-model.trim="environment.brand" :validator="validator.brand" required>
          Navigation Bar Title
        </text-input>

        <img v-if="environment.titleLogo" class="form-image-background" :src="resolveAssetPath(environment.titleLogo, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.titleLogo" legacy-manifest-path="ui/customer">Navigation Bar Logo</asset-select-input>

        <checkbox-input v-model="environment.useInternalHelp">Use Internal Support Requests</checkbox-input>
        <checkbox-input v-if="environment.useInternalHelp" v-model="environment.hideHelpFromNavbar">
          Hide Support Requests From Navbar (access via FAQ)
        </checkbox-input>
        <text-input v-if="!(environment.useInternalHelp)" label v-model.trim="environment.helpUrl">
          Contact Support (URL)
        </text-input>
        <checkbox-input v-model="environment.useInternalFaq">Use Internal FAQ</checkbox-input>
        <text-input v-if="!(environment.useInternalFaq)" label v-model.trim="environment.faqUrl">
          Help Information (URL)
        </text-input>
      </collapsible-section>

      <!-- global color settings -->
      <collapsible-section
        name="Navigation Bar Color Settings"
        id="NBCS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <div
          v-if="environment.navBarColor != null && environment.navBarColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.navBarColor};`"
        />
        <text-input
          label
          v-model.trim="environment.navBarColor"
          placeholder="rgba(17, 17, 17, 1)"
        >
          Navigation Bar Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.navBarDropdownColor != null && environment.navBarDropdownColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.navBarDropdownColor};`"
        />
        <text-input
          label
          v-model.trim="environment.navBarDropdownColor"
          placeholder="rgba(0, 0, 7, 0.76)"
        >
          Navigation Bar Dropdown Background Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.navBarHoverColor != null && environment.navBarHoverColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.navBarHoverColor};`"
        />
        <text-input
          label
          v-model.trim="environment.navBarHoverColor"
          placeholder="rgba(0, 0, 7, 0.3)"
        >
          Navigation Bar Button Hover Background Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.navBarTextColor != null && environment.navBarTextColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.navBarTextColor};`"
        />
        <text-input
          label
          v-model.trim="environment.navBarTextColor"
          placeholder="white"
        >
          Navigation Bar Text Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.faqBackgroundColor != null && environment.faqBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.faqBackgroundColor};`"
        />
        <text-input
          label
          v-model.trim="environment.faqBackgroundColor"
          placeholder="white"
        >
          FAQ Background Color (any valid CSS colors, rgba() recommended, default: white)
        </text-input>

        <div
          v-if="environment.faqTextColor != null && environment.faqTextColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.faqTextColor};`"
        />
        <text-input
          label
          v-model.trim="environment.faqTextColor"
          placeholder="black"
        >
          FAQ Text Color (any valid CSS colors, rgba() recommended, default: black)
        </text-input>
      </collapsible-section>

      <collapsible-section
        name="Hub Color Settings"
        id="HCS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <div
          v-if="environment.hubHeaderTextColor != null && environment.hubHeaderTextColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubHeaderTextColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubHeaderTextColor"
          placeholder="e.g. white (if unspecified, defaults to white)"
        >
          Hub Header Text Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.hubHeaderBackgroundColor != null && environment.hubHeaderBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubHeaderBackgroundColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubHeaderBackgroundColor"
          placeholder="e.g. rgba(17, 17, 17, 0.4) (if unspecified)"
        >
          Hub Header Background Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.hubBoxBackgroundColor != null && environment.hubBoxBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubBoxBackgroundColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubBoxBackgroundColor"
          placeholder="e.g. rgba(17, 17, 17, 0.5) (if unspecified, can fall back to gamulation settings)"
        >
          Hub Thumbnail Background Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.hubBoxTextColor != null && environment.hubBoxTextColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubBoxTextColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubBoxTextColor"
          placeholder="e.g. white (if unspecified, can fall back to gamulation settings)"
        >
          Hub Thumbnail Text Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.hubDetailsPreviewBackgroundColor != null && environment.hubDetailsPreviewBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubDetailsPreviewBackgroundColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubDetailsPreviewBackgroundColor"
          placeholder="e.g. rgba(17, 17, 17, 0.5) (if unspecified, can fall back to gamulation settings)"
        >
          Hub Details Preview Background Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.hubDetailsPreviewTextColor != null && environment.hubDetailsPreviewTextColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubDetailsPreviewTextColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubDetailsPreviewTextColor"
          placeholder="e.g. white (if unspecified, can fall back to gamulation settings)"
        >
          Hub Details Preview Text Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.hubDetailsModalBackgroundColor != null && environment.hubDetailsModalBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubDetailsModalBackgroundColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubDetailsModalBackgroundColor"
          placeholder="e.g. rgba(17, 17, 17, 0.5) (if unspecified, can fall back to gamulation settings)"
        >
          Hub Details Modal Background Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.hubDetailsModalTextColor != null && environment.hubDetailsModalTextColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.hubDetailsModalTextColor};`"
        />
        <text-input
          label
          v-model.trim="environment.hubDetailsModalTextColor"
          placeholder="e.g. white (if unspecified, can fall back to gamulation settings)"
        >
          Hub Details Modal Text Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>
      </collapsible-section>

      <collapsible-section
        name="Main Color Settings"
        id="MCS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <div
          v-if="environment.primaryColor != null && environment.primaryColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.primaryColor};`"
        />
        <text-input label v-model.trim="environment.primaryColor">
          Brand Primary Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.primaryColorLightened != null && environment.primaryColorLightened.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.primaryColorLightened};`"
        />
        <text-input label v-model.trim="environment.primaryColorLightened">
          Brand Primary Color Lightened (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.secondaryColor != null && environment.secondaryColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.secondaryColor};`"
        />
        <text-input label v-model.trim="environment.secondaryColor">
          Secondary Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.secondaryColorLightened != null && environment.secondaryColorLightened.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.secondaryColorLightened};`"
        />
        <text-input label v-model.trim="environment.secondaryColorLightened">
          Secondary Color Lightened (defaults to Secondary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.secondaryColorTransparent != null && environment.secondaryColorTransparent.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.secondaryColorTransparent};`"
        />
        <text-input label v-model.trim="environment.secondaryColorTransparent">
          Secondary Color Transparent (defaults to Secondary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.neutralColor != null && environment.neutralColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.neutralColor};`"
        />
        <text-input label v-model.trim="environment.neutralColor">
          Neutral Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.narratorIconBackgroundColor != null && environment.narratorIconBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.narratorIconBackgroundColor};`"
        />
        <text-input label v-model.trim="environment.narratorIconBackgroundColor">
          Narrator Icon Background Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.primaryBorderColor != null && environment.primaryBorderColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.primaryBorderColor};`"
        />
        <text-input label v-model.trim="environment.primaryBorderColor">
          Primary Border Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.secondaryBorderColor != null && environment.secondaryBorderColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.secondaryBorderColor};`"
        />
        <text-input label v-model.trim="environment.secondaryBorderColor">
          Secondary Border Color (defaults to Secondary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>
      </collapsible-section>

      <collapsible-section
        name="Button Color Settings"
        id="BCS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <div
          v-if="environment.buttonColor != null && environment.buttonColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.buttonColor};`"
        />
        <text-input label v-model.trim="environment.buttonColor">
          Button Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.buttonColorLightened != null && environment.buttonColorLightened.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.buttonColorLightened};`"
        />
        <text-input label v-model.trim="environment.buttonColorLightened">
          Button Color Lightened (defaults to Button Color, then Primary Color Lightened, then Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.buttonTextColor != null && environment.buttonTextColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.buttonTextColor};`"
        />
        <text-input label v-model.trim="environment.buttonTextColor">
          Button Text Color (supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <collapsible-section
          name="Choice Button Color Settings"
          id="BCS-C"
          bold
          :buttonStyle="{
            'font-size': '140%'
          }"
        >
          <div
            v-if="environment.choiceButtonColor != null && environment.choiceButtonColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.choiceButtonColor};`"
          />
          <text-input label v-model.trim="environment.choiceButtonColor">
            Choice Button Color (supports all valid CSS colors, but rgba() recommended)
          </text-input>

          <div
            v-if="environment.choiceButtonColorLightened != null && environment.choiceButtonColorLightened.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.choiceButtonColorLightened};`"
          />
          <text-input label v-model.trim="environment.choiceButtonColorLightened">
            Choice Button Color Lightened (defaults to Choice Button Color, supports all valid CSS colors, but rgba() recommended)
          </text-input>

          <div
            v-if="environment.choiceButtonActiveColor != null && environment.choiceButtonActiveColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.choiceButtonActiveColor};`"
          />
          <text-input label v-model.trim="environment.choiceButtonActiveColor">
            Choice Button Active Color (supports all valid CSS colors, but rgba() recommended)
          </text-input>

          <div
            v-if="environment.choiceButtonActiveColorLightened != null && environment.choiceButtonActiveColorLightened.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.choiceButtonActiveColorLightened};`"
          />
          <text-input label v-model.trim="environment.choiceButtonActiveColorLightened">
            Choice Button Active Color Lightened (defaults to Choice Button Active Color, supports all valid CSS colors, but rgba() recommended)
          </text-input>

          <div
            v-if="environment.choiceButtonTextColor != null && environment.choiceButtonTextColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.choiceButtonTextColor};`"
          />
          <text-input label v-model.trim="environment.choiceButtonTextColor">
            Choice Button Text Color (supports all valid CSS colors, but rgba() recommended)
          </text-input>
        </collapsible-section>

        <collapsible-section
          name="FAQ Category Button Color Settings"
          id="BCS-FC"
          bold
          :buttonStyle="{
            'font-size': '140%'
          }"
        >
          <div
            v-if="environment.categoryButtonColor != null && environment.categoryButtonColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.categoryButtonColor};`"
          />
          <text-input label v-model.trim="environment.categoryButtonColor">
            FAQ Category Button Color (any valid CSS color, rgba() recommended, fallback: Button Color)
          </text-input>

          <div
            v-if="environment.categoryButtonColorLightened != null && environment.categoryButtonColorLightened.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.categoryButtonColorLightened};`"
          />
          <text-input label v-model.trim="environment.categoryButtonColorLightened">
            FAQ Category Button Color Lightened (any valid CSS color, rgba() recommended, fallback: FAQ Category Button Color)
          </text-input>

          <div
            v-if="environment.categoryButtonTextColor != null && environment.categoryButtonTextColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.categoryButtonTextColor};`"
          />
          <text-input label v-model.trim="environment.categoryButtonTextColor">
            FAQ Category Button Text Color (any valid CSS color, rgba() recommended, fallback: Button Text Color)
          </text-input>

          <div
            v-if="environment.categoryButtonUnselectedColor != null && environment.categoryButtonUnselectedColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.categoryButtonUnselectedColor};`"
          />
          <text-input label v-model.trim="environment.categoryButtonUnselectedColor">
            FAQ Category Button Unselected Color (any valid CSS color, rgba() recommended, fallback: FAQ Category Button Color)
          </text-input>

          <div
            v-if="environment.categoryButtonUnselectedColorLightened != null && environment.categoryButtonUnselectedColorLightened.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.categoryButtonUnselectedColorLightened};`"
          />
          <text-input label v-model.trim="environment.categoryButtonUnselectedColorLightened">
            FAQ Category Button Unselected Color Lightened (any valid CSS color, rgba() recommended, fallback: FAQ Category Unselected Button Color)
          </text-input>

          <div
            v-if="environment.categoryButtonUnselectedTextColor != null && environment.categoryButtonUnselectedTextColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.categoryButtonUnselectedTextColor};`"
          />
          <text-input label v-model.trim="environment.categoryButtonUnselectedTextColor">
            FAQ Category Button Unselected Text Color (any valid CSS color, rgba() recommended, fallback: FAQ Category Button Text Color)
          </text-input>
        </collapsible-section>

        <collapsible-section
          name="FAQ Article Button Color Settings"
          id="BCS-FA"
          bold
          :buttonStyle="{
            'font-size': '140%'
          }"
        >
          <div
            v-if="environment.articleButtonColor != null && environment.articleButtonColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.articleButtonColor};`"
          />
          <text-input label v-model.trim="environment.articleButtonColor">
            FAQ Article Button Color (any valid CSS color, rgba() recommended, fallback: Button Color)
          </text-input>

          <div
            v-if="environment.articleButtonColorLightened != null && environment.articleButtonColorLightened.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.articleButtonColorLightened};`"
          />
          <text-input label v-model.trim="environment.articleButtonColorLightened">
            FAQ Article Button Color Lightened (any valid CSS color, rgba() recommended, fallback: FAQ Article Button Color)
          </text-input>

          <div
            v-if="environment.articleButtonTextColor != null && environment.articleButtonTextColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.articleButtonTextColor};`"
          />
          <text-input label v-model.trim="environment.articleButtonTextColor">
            FAQ Article Button Text Color (any valid CSS color, rgba() recommended, fallback: Button Text Color)
          </text-input>

          <div
            v-if="environment.articleButtonUnselectedColor != null && environment.articleButtonUnselectedColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.articleButtonUnselectedColor};`"
          />
          <text-input label v-model.trim="environment.articleButtonUnselectedColor">
            FAQ Article Button Unselected Color (any valid CSS color, rgba() recommended, fallback: FAQ Article Button Color)
          </text-input>

          <div
            v-if="environment.articleButtonUnselectedColorLightened != null && environment.articleButtonUnselectedColorLightened.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.articleButtonUnselectedColorLightened};`"
          />
          <text-input label v-model.trim="environment.articleButtonUnselectedColorLightened">
            FAQ Article Button Unselected Color Lightened (any valid CSS color, rgba() recommended, fallback: FAQ Article Unselected Button Color)
          </text-input>

          <div
            v-if="environment.articleButtonUnselectedTextColor != null && environment.articleButtonUnselectedTextColor.length > 0"
            :style="`height: 15px; width: 15px; background-color: ${environment.articleButtonUnselectedTextColor};`"
          />
          <text-input label v-model.trim="environment.articleButtonUnselectedTextColor">
            FAQ Article Button Unselected Text Color (any valid CSS color, rgba() recommended, fallback: FAQ Article Button Text Color)
          </text-input>
        </collapsible-section>
      </collapsible-section>

      <collapsible-section
        name="Other In-Gamulation Color Settings"
        id="OIGCS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <div style="display: flex; flex-direction: row; align-items: flex-start;">
          <div v-for="(c, i) in parsedMetricGraphBarColors(environment.metricGraphBarColors)" :key="i" :style="`height: 15px; width: 15px; background-color: ${c};`" />
        </div>
        <text-input
          label
          v-model.trim="environment.metricGraphBarColors"
          placeholder="[&quot;rgba(251, 138, 107, 1)&quot;, &quot;rgba(4, 217, 233, 1)&quot;, &quot;rgba(155, 249, 195, 1)&quot;]"
        >
          Metric Graph Bar Colors (wrap in [ ] and wrap each element in double-quotes and separate with ,)
        </text-input>

        <hr/>
        <div style="display: flex; flex-direction: row; align-items: flex-start;">
          <div v-for="(c, i) in parsedMetricGraphBarColors(environment.allocationBarColors)" :key="i" :style="`height: 15px; width: 15px; background-color: ${c};`" />
        </div>
        <text-input
          label
          v-model.trim="environment.allocationBarColors"
          placeholder="[&quot;#FFA07A&quot;, &quot;#FFD700&quot;, &quot;#3CB371&quot;, &quot;#1E90FF&quot;]"
        >
          Allocation Decision Bar Colors (wrap in [ ] and wrap each element in double-quotes and separate with ,)
        </text-input>

        <div
          v-if="environment.allocationActiveDetailsButtonColor != null && environment.allocationActiveDetailsButtonColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.allocationActiveDetailsButtonColor};`"
        />
        <text-input label v-model.trim="environment.allocationActiveDetailsButtonColor">
          Allocation Decision Active Details Button Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.allocationInactiveDetailsButtonColor != null && environment.allocationInactiveDetailsButtonColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.allocationInactiveDetailsButtonColor};`"
        />
        <text-input label v-model.trim="environment.allocationInactiveDetailsButtonColor">
          Allocation Decision Inactive Details Button Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.allocationActiveDetailsBackgroundColor != null && environment.allocationActiveDetailsBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.allocationActiveDetailsBackgroundColor};`"
        />
        <text-input label v-model.trim="environment.allocationActiveDetailsBackgroundColor">
          Allocation Decision Active Details Background Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.allocationPerformanceBarBackgroundColor != null && environment.allocationPerformanceBarBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.allocationPerformanceBarBackgroundColor};`"
        />
        <text-input label v-model.trim="environment.allocationPerformanceBarBackgroundColor">
          Allocation Performance Bar (Scoring Objectives + Key Performance Measures) Background Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <hr/>

        <div
          v-if="environment.progressBarForegroundColor != null && environment.progressBarForegroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.progressBarForegroundColor};`"
        />
        <text-input label v-model.trim="environment.progressBarForegroundColor">
          Progress Bar Foreground Color (defaults to Primary Color, supports all valid CSS colors, but rgba() recommended)
        </text-input>

        <div
          v-if="environment.progressBarBackgroundColor != null && environment.progressBarBackgroundColor.length > 0"
          :style="`height: 15px; width: 15px; background-color: ${environment.progressBarBackgroundColor};`"
        />
        <text-input label v-model.trim="environment.progressBarBackgroundColor">
          Progress Bar Background Color (defaults to Secondary Color Transparent, supports all valid CSS colors, but rgba() recommended)
        </text-input>
      </collapsible-section>

      <!-- sign in screen settings -->
      <!-- Currently no longer used -->
      <!-- <img v-if="environment.signInBoxBackground" class="form-image-background" :src="resolveAssetPath(environment.signInBoxBackground, 'ui/customer')">
      <asset-select-input use-private-assets label v-model="environment.signInBoxBackground" legacy-manifest-path="ui/customer">Title Screen Title Background</asset-select-input> -->

      <!-- title screen settings -->
      <collapsible-section
        name="Gamulation Title Screen Settings"
        id="GTSS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <text-input label v-model.trim="environment.revisionText">
          Bottom Left Title Screen Text
        </text-input>

        <text-input label v-model.trim="environment.creditsText">
          Bottom Right Title Screen Text
        </text-input>

        <img v-if="environment.creditsLogo" class="form-image-background" :src="resolveAssetPath(environment.creditsLogo, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.creditsLogo" legacy-manifest-path="ui/customer">Bottom Right Title Screen Logo</asset-select-input>

        <img v-if="environment.titleBackground" class="form-image-background" :src="resolveAssetPath(environment.titleBackground, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.titleBackground" legacy-manifest-path="ui/customer">Title Screen Background Image</asset-select-input>

        <checkbox-input v-model="environment.useLightSelectGameText">
          Use light "Select Gamulation/{{classTypeName}}" text
        </checkbox-input>
      </collapsible-section>

      <!-- default team view settings (fake team) -->
      <collapsible-section
        name="Major Decision Team View Settings"
        id="TVS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <img v-if="environment.defaultTeamBanner1" class="form-image-background" :src="resolveAssetPath(environment.defaultTeamBanner1, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.defaultTeamBanner1" legacy-manifest-path="ui/customer">Default Team Banner 1</asset-select-input>
        <img v-if="environment.defaultTeamBanner2" class="form-image-background" :src="resolveAssetPath(environment.defaultTeamBanner2, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.defaultTeamBanner2" legacy-manifest-path="ui/customer">Default Team Banner 2</asset-select-input>
        <img v-if="environment.defaultTeamBanner3" class="form-image-background" :src="resolveAssetPath(environment.defaultTeamBanner3, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.defaultTeamBanner3" legacy-manifest-path="ui/customer">Default Team Banner 3</asset-select-input>
        <img v-if="environment.defaultTeamBanner4" class="form-image-background" :src="resolveAssetPath(environment.defaultTeamBanner4, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.defaultTeamBanner4" legacy-manifest-path="ui/customer">Default Team Banner 4</asset-select-input>
      </collapsible-section>

      <!-- result screen settings -->
      <collapsible-section
        name="Results Screen Settings"
        id="RSS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <text-input label v-model.trim="environment.resultScreenHeader">Result Screen Header</text-input>
      </collapsible-section>

      <!-- game completed screen settings -->
      <collapsible-section
        name="Gamulation End Settings"
        id="GES"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <text-input label v-model.trim="environment.gameEndHeader">Gamulation End Header</text-input>

        <text-input label v-model.trim="environment.gameEndSubheader">Gamulation End Subheader</text-input>

        <img v-if="environment.gameEndLogo" class="form-image-background" :src="resolveAssetPath(environment.gameEndLogo, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.gameEndLogo" legacy-manifest-path="ui/customer">Gamulation End Logo</asset-select-input>

        <character-select-input
          label
          select-only-id
          v-model="environment.gameEndCharacter"
          :default-filters="{ narrator: true, avatar: true }"
          use="_id"
          display="name"
          :to="canManageGames ? ((id) => { return `/blade/characters/${id}/edit`; }) : undefined"
        >
          Gamulation End Screen Character (defaults to player avatar)
        </character-select-input>

        <select-input
          label
          v-model="environment.gameEndCharacterVariant"
          :options="variantOptions(characters, variants, environment.gameEndCharacter)"
        >
          Gamulation End Screen Character Variant (defaults to Forward Right)
        </select-input>

        <img
          v-if="environment.gameEndBackground"
          class="form-image-background"
          :src="resolveAssetPath(environment.gameEndBackground, 'backgrounds')"
        >
        <asset-select-input
          class="col-sm-6"
          v-model="environment.gameEndBackground"
          legacy-manifest-path="backgrounds"
        >Gamulation End Background</asset-select-input>
      </collapsible-section>
      <!-- report settings -->
      <collapsible-section
        name="Report Settings"
        id="RS"
        bold
        :buttonStyle="{
          'font-size': '140%'
        }"
      >
        <img v-if="environment.reportLogo" class="form-image-background" :src="resolveAssetPath(environment.reportLogo, 'ui/customer')">
        <asset-select-input use-private-assets label v-model="environment.reportLogo" legacy-manifest-path="ui/customer">Report Logo (falls back to Credits Logo then Brand Logo if not specified)</asset-select-input>

        <text-input
          label
          v-model.trim="environment.reportTitle"
        >Report Title (if unspecified, uses the default for the report type)</text-input>

        <text-input label v-model.trim="environment.reportFooter">Report Footer</text-input>

        <img
          v-if="environment.reportFooterLogo"
          class="form-image-background"
          :src="resolveAssetPath(environment.reportFooterLogo, 'ui/customer')"
        >
        <asset-select-input
          use-private-assets
          label
          v-model="environment.reportFooterLogo"
          legacy-manifest-path="ui/customer"
        >Report Footer Logo (optional, no fallback)</asset-select-input>

        <text-input label v-model.trim="environment.course">
          Course (this setting overridden by active workgroup/class)
        </text-input>

        <text-input label v-model.trim="environment.professor">
          Professor (this setting overridden by active workgroup/class)
        </text-input>
      </collapsible-section>
    </template>
  </edit-page>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';

import EditPage from '../../ui/pages/EditPage';

import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import CollapsibleSection from '../../ui/inputs/CollapsibleSection';
import CharacterSelectInput from '../../ui/inputs/CharacterSelectInput';

export default {
  components: {
    EditPage,
    AssetSelectInput,
    SelectInput,
    TextInput,
    CheckboxInput,
    CollapsibleSection,
    CharacterSelectInput,
  },
  data() {
    return {
      utils: {},
      environment: {
        // metadata
        _id: '',
        name: '',
        active: false,
        course: '',
        professor: '',
        // global settings
        tabText: '',
        brand: '',
        titleLogo: '', // now used for brand logo
        helpUrl: '',
        reasoningInputOverride: '',
        // global color settings
        navBarColor: '',
        navBarTextColor: '',
        faqBackgroundColor: 'white',
        faqTextColor: 'black',
        primaryColor: 'rgba(205, 120, 81, 1)',
        primaryColorLightened: 'rgba(245, 155, 115, 1)',
        neutralColor: 'rgba(130, 100, 50, 1)',
        narratorIconBackgroundColor: 'rgba(170, 135, 84, 1)',
        choiceButtonColor: 'rgba(204, 204, 204, 1)',
        choiceButtonColorLightened: 'rgba(204, 204, 204, 1)',
        choiceButtonActiveColor: 'rgba(255, 255, 255, 1)',
        choiceButtonActiveColorLightened: 'rgba(255, 255, 255, 1)',
        choiceButtonTextColor: 'rgba(56, 70, 80, 1)',
        buttonColor: 'rgba(205, 120, 81, 1)',
        buttonColorLightened: 'rgba(245, 155, 115, 1)',
        buttonTextColor: 'rgba(255, 255, 255, 1)',
        categoryButtonColor: 'rgba(205, 120, 81, 1)',
        categoryButtonColorLightened: 'rgba(245, 155, 115, 1)',
        categoryButtonTextColor: 'rgba(255, 255, 255, 1)',
        categoryButtonUnselectedColor: 'rgba(205, 120, 81, 1)',
        categoryButtonUnselectedColorLightened: 'rgba(245, 155, 115, 1)',
        categoryButtonUnselectedTextColor: 'rgba(255, 255, 255, 1)',
        articleButtonColor: 'rgba(205, 120, 81, 1)',
        articleButtonColorLightened: 'rgba(245, 155, 115, 1)',
        articleButtonTextColor: 'rgba(255, 255, 255, 1)',
        articleButtonUnselectedColor: 'rgba(205, 120, 81, 1)',
        articleButtonUnselectedColorLightened: 'rgba(245, 155, 115, 1)',
        articleButtonUnselectedTextColor: 'rgba(255, 255, 255, 1)',
        secondaryColor: 'rgba(205, 120, 81, 1)',
        secondaryColorLightened: 'rgba(245, 155, 115, 1)',
        secondaryColorTransparent: 'rgba(205, 120, 81, 0.35)',
        primaryBorderColor: 'rgba(205, 120, 81, 1)',
        secondaryBorderColor: 'rgba(205, 120, 81, 1)',
        metricGraphBarColors: '["rgba(251, 138, 107, 1)", "rgba(4, 217, 233, 1)", "rgba(155, 249, 195, 1)"]',
        allocationBarColors: '["#FFA07A", "#FFD700", "#3CB371", "#1E90FF"]',
        progressBarForegroundColor: 'rgba(205, 120, 81, 1)',
        progressBarBackgroundColor: 'rgba(205, 120, 81, 0.35)',
        // sign in screen settings
        signInText: '',
        signInBackground: '',
        signInLogo: '',
        signInTextColor: 'rgba(0, 0, 0, 1)',
        signInBoxBackground: '',
        // title screen settings
        revisionText: '',
        creditsText: '',
        creditsLogo: '',
        titleImage: '',
        titleBackground: '',
        useLightSelectGameText: false,
        // game completed screen settings
        gameEndHeader: '',
        gameEndSubheader: '',
        gameEndLogo: '',
        gameEndCharacter: '',
        gameEndCharacterVariant: '',
        gameEndBackground: '',
        // default team view settings (fake team)
        defaultTeamBanner1: '',
        defaultTeamBanner2: '',
        defaultTeamBanner3: '',
        defaultTeamBanner4: '',
        // result screen settings
        resultScreenHeader: '',
        // report settings
        reportLogo: '',
        reportTitle: '',
        reportFooter: '',
      },
      reasoningInputOverrideOptions: [],
      validator: {
        name: { required },
        tabText: { required },
        brand: { required },
        reasoningInputOverride: { required },
      }
    };
  },
  computed: {
    ...mapGetters([
      'classTypeName',
      'canManageGames',
    ]),
  },
  methods: {
    ...mapActions([
      'getEnvironment',
      'getEnvironmentTypes',
      'getEnvironmentEnums',
      'createEnvironment',
      'updateEnvironment',
      'deleteEnvironment'
    ]),
    // Note: This is also used for "Allocation Decision Bar Colors"
    parsedMetricGraphBarColors(input) {
      let result = [];
      try {
        result = JSON.parse(input);
        return result;
      } catch (err) {
        return [];
      }
    },
    variantOptions(characters, variants, characterId) {
      const c = characters.find(ch => ch._id === characterId);
      if (c) {
        if (c.imageVariants && c.imageVariants.length > 0) {
          return c.imageVariants.map(v => v.name);
        }
        return [];
      }
      return variants;
    },
  },
  async mounted() {
    const { reasoningInputOverrideOptions } = await this.getEnvironmentEnums();
    this.reasoningInputOverrideOptions = reasoningInputOverrideOptions;
  }
};
</script>

<style lang="scss" scoped>
.form-image-background + span {
  display: block;
  margin: -10px 0 10px 10px;
}
.form-image-background {
  background-color: rgba(0, 0, 0, 0);
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/EnvironmentEdit.vue